import React, { useState, useEffect, useCallback } from 'react';
import { Gift, Star, ChevronRight, Check, Loader } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function GiftCardRewardSection({ user, api }) {
  const [status, setStatus] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [success, setSuccess] = useState(null);
  const [step, setStep] = useState('status'); // 'status' | 'select' | 'success'

  const isPro = user?.current_plan === 'pro' || user?.current_plan === 'family';

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get('/giftcards/reward-status');
      setStatus(res.data);
    } catch (e) { console.error('Fetch gift card status:', e); }
  }, [api]);

  const fetchProducts = useCallback(async () => {
    try {
      const res = await api.get('/giftcards/products?country_code=DE');
      setProducts(res.data?.content || []);
    } catch (e) { console.error('Fetch gift card products:', e); }
  }, [api]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleClaimClick = async () => {
    await fetchProducts();
    setStep('select');
  };

  const handleClaim = async () => {
    if (!selectedProduct) return;
    setClaiming(true);
    try {
      const res = await api.post(`/giftcards/claim-reward?product_id=${selectedProduct.productId}`);
      setSuccess(res.data.message);
      setStep('success');
      fetchStatus();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Fehler beim Einlösen. Bitte versuche es erneut.';
      setSuccess('error:' + msg);
      setStep('success');
    } finally { setClaiming(false); }
  };

  if (!status) return null;

  const streak = status.login_streak || 0;
  const pending = status.gift_card_reward_pending;
  const nextIn = status.next_reward_in_days || (30 - (streak % 30));
  const progress = streak % 30 === 0 && streak > 0 ? 30 : streak % 30;

  return (
    <div className="mb-4 p-4 rounded-2xl border" style={{ borderColor: pending ? 'var(--primary)' : 'var(--border)', background: pending ? 'rgba(var(--primary-rgb, 99,102,241), 0.05)' : 'var(--secondary)' }}>
      <div className="flex items-center gap-2 mb-3">
        <Gift className="w-5 h-5" style={{ color: pending ? 'var(--primary)' : 'var(--muted-foreground)' }} />
        <span className="font-bold text-sm">Tägliche Login-Belohnung</span>
        {isPro && <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}>Pro</span>}
      </div>

      {!isPro ? (
        <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
          Upgrade auf Pro um nach 30 täglichen Logins eine echte Geschenkkarte (€10) zu verdienen!
        </div>
      ) : (
        <AnimatePresence mode="wait">
          {step === 'status' && (
            <motion.div key="status" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                  {pending ? '🎉 Deine Belohnung wartet!' : `Noch ${nextIn} Tage bis zur nächsten Karte`}
                </span>
                <span className="text-xs font-bold">{pending ? '30/30' : `${progress}/30`}</span>
              </div>
              <div className="w-full rounded-full h-2 mb-3" style={{ background: 'var(--border)' }}>
                <motion.div className="h-2 rounded-full" style={{ background: 'var(--primary)', width: `${pending ? 100 : (progress / 30) * 100}%` }} initial={{ width: 0 }} animate={{ width: `${pending ? 100 : (progress / 30) * 100}%` }} />
              </div>
              {pending && (
                <button onClick={handleClaimClick} className="w-full py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2" style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }} data-testid="claim-reward-btn">
                  <Gift className="w-4 h-4" /> Geschenkkarte auswählen & einlösen <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </motion.div>
          )}

          {step === 'select' && (
            <motion.div key="select" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
              <p className="text-xs mb-3 font-semibold" style={{ color: 'var(--muted-foreground)' }}>Wähle deine €10 Geschenkkarte:</p>
              <div className="space-y-2 max-h-52 overflow-y-auto pr-1 mb-3">
                {products.length === 0 ? <div className="text-xs text-center py-4"><Loader className="w-4 h-4 animate-spin mx-auto mb-1" />Lade Produkte...</div> : products.map(p => (
                  <button key={p.productId} onClick={() => setSelectedProduct(p)} className="w-full p-3 rounded-xl text-left flex items-center gap-3 border transition-all" style={{ borderColor: selectedProduct?.productId === p.productId ? 'var(--primary)' : 'var(--border)', background: selectedProduct?.productId === p.productId ? 'rgba(var(--primary-rgb,99,102,241),0.08)' : 'var(--background)' }} data-testid={`giftcard-product-${p.productId}`}>
                    {selectedProduct?.productId === p.productId && <Check className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--primary)' }} />}
                    {selectedProduct?.productId !== p.productId && <div className="w-4 h-4 rounded-full border flex-shrink-0" style={{ borderColor: 'var(--border)' }} />}
                    <div>
                      <div className="text-sm font-semibold">{p.productName}</div>
                      <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{p.recipientCurrencyCode} · Digital Code</div>
                    </div>
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setStep('status')} className="flex-1 py-2 rounded-xl text-sm border" style={{ borderColor: 'var(--border)' }}>Zurück</button>
                <button onClick={handleClaim} disabled={!selectedProduct || claiming} className="flex-2 py-2 px-4 rounded-xl text-sm font-bold flex items-center justify-center gap-2" style={{ background: selectedProduct ? 'var(--primary)' : 'var(--border)', color: selectedProduct ? 'var(--primary-foreground)' : 'var(--muted-foreground)' }} data-testid="confirm-claim-btn">
                  {claiming ? <Loader className="w-4 h-4 animate-spin" /> : <><Gift className="w-4 h-4" /> Einlösen</>}
                </button>
              </div>
            </motion.div>
          )}

          {step === 'success' && (
            <motion.div key="success" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-2">
              {success?.startsWith('error:') ? (
                <>
                  <div className="text-2xl mb-2">⚠️</div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--accent)' }}>{success.replace('error:', '')}</p>
                </>
              ) : (
                <>
                  <div className="text-3xl mb-2">🎉</div>
                  <p className="text-sm font-semibold">{success}</p>
                  <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>Schau in dein Email-Postfach!</p>
                </>
              )}
              <button onClick={() => setStep('status')} className="mt-3 text-xs px-4 py-2 rounded-xl" style={{ background: 'var(--secondary)' }}>Schließen</button>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </div>
  );
}
